import { LayerKind } from '@shared';
import type {
  DecisionSupportParcelData,
  DecisionSupportZoneData,
  ParcelStatData,
  StatisticsDetailsParcel,
  YieldAppliedDoseParcelData,
  YieldAppliedDoseZoneData,
  YieldSoilParameterParameterData,
  YieldSoilParameterParcelData,
  YieldStemNumberParcelData,
  YieldStemNumberZoneData,
  ZoneStatData,
  ZoneStrengthDeltaZoneData
} from '../models';
import { LayerGroupKind } from '../../geo-layers-upload/enums';
import {
  currencyMapInfoValueFn,
  currencyValueZoneLabelFn,
  decisionSupportYieldMapInfoValueFn,
  decisionSupportYieldValueZoneLabelFn,
  defaultStatisticsMapInfoValueFn,
  defaultStatisticsValueZoneLabelFn,
  tenthRoundedMapInfoValueFn,
  tenthRoundedValueZoneLabelFn,
  type DataTableSort,
  type VectorMapLayer,
  type VectorLayerZone,
  type MapLayer
} from '@common';
import { StatisticsKind, StatisticsView } from '../enums';
import { roundToThousand } from '@core';

const threeDecimalValueZoneLabelFn = (layer: VectorMapLayer, zone: VectorLayerZone) => {
  const soilParameter = layer.customData?.soilParameter as YieldSoilParameterParameterData | undefined;
  const stdBeta = soilParameter?.stdBeta ?? zone.value;
  if (stdBeta === undefined) return '';
  const stdBetaStr = Number(stdBeta).toFixed(3);

  if (soilParameter?.r2 === undefined) return `STD.β: ${stdBetaStr}`;
  const r2 = Number(soilParameter.r2).toFixed(3);
  return `STD.β: ${stdBetaStr}\nR²: ${r2}`;
};

const threeDecimalMapInfoValueFn = (layer: MapLayer, title: string) => {
  const num = parseFloat(title);
  const formatted = isNaN(num) ? title : num.toFixed(3);
  return (layer.data as any).unitType ? `${formatted} ${(layer.data as any).unitType}` : formatted;
};

const zoneStrengthDeltaSpecificStatDataHandler: Partial<
  Record<LayerKind, (zone: ZoneStrengthDeltaZoneData, value: any) => void>
> = {
  [LayerKind.statisticZoneStrengthDelta]: (zone, value) => {
    zone.delta = value;
  },
  [LayerKind.statisticYieldRank]: (zone, value) => {
    zone.yieldRank = value;
  },
  [LayerKind.statisticZoneStrengthRank]: (zone, value) => {
    zone.zoneStrengthRank = value;
  },
  [LayerKind.yieldValue]: (zone, value) => {
    zone.yieldValue = value;
  },
  [LayerKind.statisticZoneStrength]: (zone, value) => {
    zone.zoneStrength = value;
  }
};

const yieldStemNumberSpecificStatDataHandler: Partial<
  Record<LayerKind, (zone: YieldStemNumberZoneData | YieldStemNumberParcelData, value: any) => void>
> = {
  [LayerKind.stemNumber]: (zone, value) => {
    zone.stemNumber = value;
  },
  [LayerKind.yieldValue]: (zone, value) => {
    zone.yieldValue = value;
  },
  [LayerKind.yieldVsStemNumber]: (zone, value) => {
    zone.yieldVsStemNumber = value;
  }
};

const yieldAppliedDoseSpecificStatDataHandler: Partial<
  Record<LayerKind, (zone: YieldAppliedDoseZoneData | YieldAppliedDoseParcelData, value: any) => void>
> = {
  [LayerKind.yieldValue]: (zone, value) => {
    zone.yieldValue = value;
  },
  [LayerKind.yieldVsK]: (zone, value) => {
    zone.yieldVsK = value;
  },
  [LayerKind.yieldVsP]: (zone, value) => {
    zone.yieldVsP = value;
  },
  [LayerKind.yieldVsN]: (zone, value) => {
    zone.yieldVsN = value;
  }
};

const decisionSupportSpecificStatDataHandler: Partial<
  Record<LayerKind, (zone: DecisionSupportZoneData | DecisionSupportParcelData, value: any) => void>
> = {
  [LayerKind.decisionSupportEstimatedRevenue]: (zone, value) => {
    zone.estimatedRevenue = value;
    zone.estimatedRevenuePerHectare = roundToThousand(value / zone.area);
  },
  [LayerKind.decisionSupportEstimatedIncome]: (zone, value) => {
    zone.estimatedIncome = value;
    zone.estimatedIncomePerHectare = roundToThousand(value / zone.area);
  },
  [LayerKind.decisionSupportCropPrice]: (zone, value) => {
    zone.cropPrice = value;
  },
  [LayerKind.decisionSupportUpkeepCost]: (zone, value) => {
    zone.upkeepCost = value;
  },
  [LayerKind.yieldValue]: (zone, value) => {
    zone.yieldValue = value;
  }
};

export const getLabelFnByLayerKind = (layerKind: LayerKind, statisticsKind: StatisticsKind) => {
  if (statisticsKind === StatisticsKind.YIELD_SOIL_PARAMETER) {
    return { zoneLabelFn: threeDecimalValueZoneLabelFn, mapInfoValueFn: threeDecimalMapInfoValueFn };
  }

  switch (layerKind) {
    case LayerKind.decisionSupportCropPrice:
    case LayerKind.decisionSupportEstimatedIncome:
    case LayerKind.decisionSupportEstimatedRevenue:
    case LayerKind.decisionSupportUpkeepCost:
      return { zoneLabelFn: currencyValueZoneLabelFn, mapInfoValueFn: currencyMapInfoValueFn };
    case LayerKind.yieldValue:
      return statisticsKind === StatisticsKind.DECISION_SUPPORT
        ? { zoneLabelFn: decisionSupportYieldValueZoneLabelFn, mapInfoValueFn: decisionSupportYieldMapInfoValueFn }
        : { zoneLabelFn: tenthRoundedValueZoneLabelFn, mapInfoValueFn: tenthRoundedMapInfoValueFn };
    default:
      return { zoneLabelFn: defaultStatisticsValueZoneLabelFn, mapInfoValueFn: defaultStatisticsMapInfoValueFn };
  }
};

const setZoneStrengthDeltaStatData = (zone: ZoneStrengthDeltaZoneData, subLayerKind: LayerKind, value: any) => {
  zoneStrengthDeltaSpecificStatDataHandler[subLayerKind]?.(zone, value);
};

const setYieldStemNumberStatData = (
  zone: YieldStemNumberZoneData | YieldStemNumberParcelData,
  subLayerKind: LayerKind,
  value: any
) => {
  yieldStemNumberSpecificStatDataHandler[subLayerKind]?.(zone, value);
};

const setYieldAppliedDoseStatData = (
  zone: YieldAppliedDoseZoneData | YieldAppliedDoseParcelData,
  subLayerKind: LayerKind,
  value: any
) => {
  yieldAppliedDoseSpecificStatDataHandler[subLayerKind]?.(zone, value);
};

const setDecisionSupportStatData = (
  zone: DecisionSupportZoneData | DecisionSupportParcelData,
  subLayerKind: LayerKind,
  value: any
) => {
  decisionSupportSpecificStatDataHandler[subLayerKind]?.(zone, value);
};

const isYieldSoilParameterParameterData = (value: unknown): value is YieldSoilParameterParameterData => {
  if (!value || typeof value !== 'object') return false;

  const { stdBeta, r2, pValue } = value as Partial<YieldSoilParameterParameterData>;
  return typeof stdBeta === 'number' && typeof r2 === 'number' && typeof pValue === 'number';
};

const setYieldSoilParameterStatData = (parcelData: YieldSoilParameterParcelData, soilParameter?: unknown) => {
  if (!isYieldSoilParameterParameterData(soilParameter)) return;

  parcelData.parameters = [
    ...(parcelData.parameters ?? []),
    {
      ...soilParameter,
      stdBeta: parseFloat(soilParameter.stdBeta.toFixed(3)),
      r2: parseFloat(soilParameter.r2.toFixed(3)),
      pValue: parseFloat(soilParameter.pValue.toFixed(3))
    }
  ];
};

const zoneStatSpecificDataHandler: Partial<
  Record<LayerGroupKind, (zone: ZoneStatData, subLayerKind: LayerKind, value: any) => void>
> = {
  [LayerGroupKind.STATISTICS_YIELD_ZONE_STRENGTH]: (zone, subLayerKind, value) => {
    setZoneStrengthDeltaStatData(zone as ZoneStrengthDeltaZoneData, subLayerKind, value);
  },
  [LayerGroupKind.STATISTICS_YIELD_STEM_NUMBER]: (zone, subLayerKind, value) => {
    setYieldStemNumberStatData(zone as YieldStemNumberZoneData, subLayerKind, value);
  },
  [LayerGroupKind.STATISTICS_YIELD_APPLIED_DOSE]: (zone, subLayerKind, value) => {
    setYieldAppliedDoseStatData(zone as YieldAppliedDoseZoneData, subLayerKind, value);
  },
  [LayerGroupKind.STATISTICS_DECISION_SUPPORT]: (zone, subLayerKind, value) => {
    setDecisionSupportStatData(zone as DecisionSupportZoneData, subLayerKind, value);
  }
};

const parcelStatSpecificDataHandler: Partial<
  Record<LayerGroupKind, (zone: ParcelStatData, subLayerKind: LayerKind, value: any) => void>
> = {
  [LayerGroupKind.STATISTICS_YIELD_STEM_NUMBER]: (zone, subLayerKind, value) => {
    setYieldStemNumberStatData(zone as YieldStemNumberParcelData, subLayerKind, value);
  },
  [LayerGroupKind.STATISTICS_YIELD_APPLIED_DOSE]: (zone, subLayerKind, value) => {
    setYieldAppliedDoseStatData(zone as YieldAppliedDoseParcelData, subLayerKind, value);
  },
  [LayerGroupKind.STATISTICS_DECISION_SUPPORT]: (zone, subLayerKind, value) => {
    setDecisionSupportStatData(zone as DecisionSupportParcelData, subLayerKind, value);
  },
  [LayerGroupKind.STATISTICS_YIELD_SOIL_PARAMETER]: (zone, _subLayerKind, value) => {
    setYieldSoilParameterStatData(zone as YieldSoilParameterParcelData, value);
  }
};

export const setZoneStatSpecificData = (
  zone: ZoneStatData,
  layerGroupKind: LayerGroupKind,
  subLayerKind: LayerKind,
  value: any
) => {
  zoneStatSpecificDataHandler[layerGroupKind]?.(zone, subLayerKind, value);
};

export const setParcelStatSpecificData = (
  zone: ParcelStatData,
  layerGroupKind: LayerGroupKind,
  subLayerKind: LayerKind,
  value: any
) => {
  parcelStatSpecificDataHandler[layerGroupKind]?.(zone, subLayerKind, value);
};

export const sortByValue = (firstValue: any, secondValue: any, sort: DataTableSort) => {
  if (firstValue < secondValue) return sort.direction === 'asc' ? -1 : 1;
  if (firstValue > secondValue) return sort.direction === 'asc' ? 1 : -1;
  return 0;
};

export const getMinMaxFromActiveSubLayer = (
  parcels: StatisticsDetailsParcel<ParcelStatData, ZoneStatData>[],
  statView: StatisticsView
) => {
  // Extract all values from activeSubLayer of zoneMapLayerGroup
  const allValues: number[] = parcels
    .map((parcel) =>
      statView === StatisticsView.ZONE
        ? parcel.zoneMapLayerGroup?.activeSubLayer?.data?.values || []
        : parcel.parcelMapLayerGroup?.activeSubLayer?.data?.values || []
    )
    .flat(); // Flatten the array of arrays into a single array

  if (allValues.length === 0) {
    return; // Handle case where no values are found
  }

  // Find the minimum and maximum values
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);

  return { min, max };
};
